/**
 * Reads `#salary-role-page-initial` JSON emitted by this page's SSR render.
 *
 * This project's custom pages/+onRenderClient.ts hydrates by manually building
 * `React.createElement(Layout, {}, React.createElement(Page))` — it never calls vike-react's
 * getPageElement(), so pages are never wrapped in VikeReactProviderPageContext. That means
 * useData()/usePageContext() reliably return undefined on the client (confirmed: SSR/curl shows
 * full content, but the client-hydrated DOM is empty because `if (!data) return null` fires).
 * Every other content page in this app (job detail, SRP, best-companies) works around this the
 * same way: embed the SSR data in a plain <script type="application/json"> tag and read it
 * synchronously via document.getElementById on first client render — see
 * pages/best-companies/parseBestCompaniesHubInitialScript.ts for the sibling pattern.
 */
import type { Data } from './+data.server';

export const SALARY_ROLE_PAGE_SCRIPT_ID = 'salary-role-page-initial';

export function getSalaryRoleDataFromScript(): Data | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.getElementById(SALARY_ROLE_PAGE_SCRIPT_ID);
  if (!el || el.tagName !== 'SCRIPT') return undefined;
  try {
    const raw = (el as HTMLScriptElement).textContent?.trim() ?? '';
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Data;
    if (!parsed || typeof parsed !== 'object') return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}
